import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { messagingAPI } from '../../lib/api';
import { Send, Paperclip, FileText, Link2, X, Download, ImageIcon, File, Upload } from 'lucide-react';
import { toast } from 'sonner';

function timeDisplay(dateStr) {
  const d = new Date(dateStr);
  return d.toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' });
}

function dateLabel(dateStr) {
  const d = new Date(dateStr);
  const today = new Date();
  if (d.toDateString() === today.toDateString()) return 'Hoy';
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return 'Ayer';
  return d.toLocaleDateString('es', { day: 'numeric', month: 'short' });
}

function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

function getFileIcon(file) {
  if (file.type?.startsWith('image/')) return ImageIcon;
  return File;
}

export default function DirectChatDialog({ open, onClose, classId, otherUser, currentUser }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [pendingFile, setPendingFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);
  const wsRef = useRef(null);
  const dragCounter = useRef(0);

  useEffect(() => {
    if (!open || !otherUser) return;
    const load = async () => {
      try {
        const res = await messagingAPI.getMessages(classId, otherUser.id);
        setMessages(res.data || []);
      } catch {}
    };
    load();
    return () => { setPendingFile(null); setIsDragging(false); dragCounter.current = 0; };
  }, [open, classId, otherUser]);

  useEffect(() => {
    if (!open || !currentUser?.token) return;
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const wsUrl = backendUrl.replace('https://', 'wss://').replace('http://', 'ws://');
    const ws = new WebSocket(`${wsUrl}/api/ws/notifications?token=${currentUser.token}`);
    ws.onopen = () => { wsRef.current = ws; };
    ws.onmessage = (event) => {
      if (event.data === 'pong') return;
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'direct_message' && data.message && data.message.sender_id === otherUser?.id) {
          setMessages(prev => [...prev, data.message]);
        }
      } catch {}
    };
    ws.onclose = () => { wsRef.current = null; };
    ws.onerror = () => ws.close();
    return () => { if (wsRef.current) wsRef.current.close(); };
  }, [open, currentUser?.token, otherUser?.id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      const urlRegex = /^https?:\/\/\S+$/;
      const msgType = urlRegex.test(text.trim()) ? 'link' : 'text';
      const res = await messagingAPI.sendMessage(classId, otherUser.id, {
        content: text.trim(),
        message_type: msgType,
      });
      setMessages(prev => [...prev, res.data]);
      setText('');
    } catch {
      toast.error('Error al enviar mensaje');
    } finally { setSending(false); }
  };

  const processFile = useCallback((file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('El archivo no puede superar 10MB');
      return;
    }
    // Show preview
    const preview = { file, name: file.name, size: file.size, type: file.type, isImage: file.type?.startsWith('image/') };
    if (preview.isImage) {
      const reader = new FileReader();
      reader.onload = (e) => { preview.thumbnailUrl = e.target.result; setPendingFile({ ...preview }); };
      reader.readAsDataURL(file);
    } else {
      setPendingFile(preview);
    }
  }, []);

  const uploadPendingFile = async () => {
    if (!pendingFile) return;
    setUploading(true);
    setUploadProgress(10);
    try {
      const formData = new FormData();
      formData.append('file', pendingFile.file);
      setUploadProgress(40);
      const res = await messagingAPI.uploadFile(classId, otherUser.id, formData);
      setUploadProgress(100);
      setMessages(prev => [...prev, res.data]);
      setPendingFile(null);
    } catch {
      toast.error('Error al subir archivo');
    } finally {
      setUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleFileInput = (e) => {
    processFile(e.target.files?.[0]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Drag & Drop
  const handleDragEnter = (e) => {
    e.preventDefault(); e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer.types.includes('Files')) setIsDragging(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault(); e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) setIsDragging(false);
  };
  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };
  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    setIsDragging(false);
    dragCounter.current = 0;
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  };

  // Paste handler (for images)
  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) processFile(file);
        return;
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (pendingFile) uploadPendingFile();
      else handleSend();
    }
  };

  if (!otherUser) return null;
  let lastDate = '';

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg h-[80vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-4 py-3 border-b border-slate-200 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-slate-200 flex items-center justify-center overflow-hidden flex-shrink-0">
              {otherUser.photo_url ? (
                <img src={otherUser.photo_url} alt="" className="w-full h-full object-cover" />
              ) : (
                <span className="text-sm font-semibold text-slate-600">{otherUser.name?.charAt(0).toUpperCase()}</span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <DialogTitle className="text-sm font-semibold truncate">{otherUser.name}</DialogTitle>
              <p className="text-xs text-slate-500">{otherUser.role === 'professor' ? 'Profesor' : 'Estudiante'}</p>
            </div>
          </div>
        </DialogHeader>

        {/* Messages area with drag & drop zone */}
        <div
          ref={dropZoneRef}
          className="flex-1 overflow-y-auto px-4 py-3 space-y-1 bg-slate-50 relative"
          data-testid="chat-messages"
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          {/* Drag overlay */}
          {isDragging && (
            <div className="absolute inset-0 z-10 bg-blue-50/90 backdrop-blur-sm border-2 border-dashed border-blue-400 rounded-lg flex flex-col items-center justify-center pointer-events-none" data-testid="drag-overlay">
              <Upload className="w-10 h-10 text-blue-500 mb-2 animate-bounce" />
              <p className="text-sm font-semibold text-blue-600">Suelta el archivo aquí</p>
              <p className="text-xs text-blue-400">Máximo 10MB</p>
            </div>
          )}

          {messages.length === 0 && (
            <div className="text-center py-12 text-slate-400">
              <Send className="w-8 h-8 mx-auto mb-2 opacity-30 rotate-[-30deg]" />
              <p className="text-sm">Inicia la conversación</p>
            </div>
          )}
          {messages.map((msg) => {
            const isMine = msg.sender_id === currentUser?.id;
            const msgDate = dateLabel(msg.created_at);
            let showDate = false;
            if (msgDate !== lastDate) { showDate = true; lastDate = msgDate; }

            return (
              <React.Fragment key={msg.id}>
                {showDate && (
                  <div className="flex justify-center py-2">
                    <span className="text-[10px] bg-slate-200 text-slate-500 px-2 py-0.5 rounded-full">{msgDate}</span>
                  </div>
                )}
                <div className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] rounded-2xl px-3 py-2 ${
                    isMine ? 'bg-blue-500 text-white rounded-br-md' : 'bg-white text-slate-800 border border-slate-200 rounded-bl-md'
                  }`}>
                    {msg.message_type === 'image' && msg.file_url && (
                      <a href={msg.file_url} target="_blank" rel="noopener noreferrer">
                        <img src={msg.file_url} alt={msg.file_name || 'imagen'} className="rounded-lg max-h-48 mb-1" />
                      </a>
                    )}
                    {msg.message_type === 'file' && msg.file_url && (
                      <a href={msg.file_url} target="_blank" rel="noopener noreferrer"
                        download={msg.file_name || 'archivo'}
                        className={`flex items-center gap-2 p-2 rounded-lg mb-1 ${isMine ? 'bg-blue-600' : 'bg-slate-100'}`}
                        data-testid="file-attachment-link">
                        <FileText className="w-5 h-5 flex-shrink-0" />
                        <span className="text-xs truncate max-w-[180px]">{msg.file_name || 'Archivo'}</span>
                        <Download className="w-4 h-4 flex-shrink-0" />
                      </a>
                    )}
                    {msg.message_type === 'link' && (
                      <a href={msg.content} target="_blank" rel="noopener noreferrer"
                        className={`flex items-center gap-1.5 ${isMine ? 'text-blue-100 underline' : 'text-blue-600 underline'}`}>
                        <Link2 className="w-3.5 h-3.5 flex-shrink-0" />
                        <span className="text-sm truncate">{msg.content}</span>
                      </a>
                    )}
                    {msg.message_type === 'text' && msg.content && (
                      <p className="text-sm whitespace-pre-wrap break-words">{msg.content}</p>
                    )}
                    {(msg.message_type === 'image' || msg.message_type === 'file') && msg.content && (
                      <p className="text-sm whitespace-pre-wrap break-words">{msg.content}</p>
                    )}
                    <p className={`text-[10px] mt-0.5 ${isMine ? 'text-blue-200' : 'text-slate-400'} text-right`}>
                      {timeDisplay(msg.created_at)}
                    </p>
                  </div>
                </div>
              </React.Fragment>
            );
          })}
          <div ref={messagesEndRef} />
        </div>

        {/* Pending file preview */}
        {pendingFile && (
          <div className="px-3 py-2 border-t border-slate-100 bg-slate-50" data-testid="file-preview">
            <div className="flex items-center gap-3 p-2 bg-white rounded-xl border border-slate-200">
              {/* Thumbnail or icon */}
              {pendingFile.isImage && pendingFile.thumbnailUrl ? (
                <img src={pendingFile.thumbnailUrl} alt="" className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />
              ) : (
                <div className="w-12 h-12 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                  {React.createElement(getFileIcon(pendingFile), { className: 'w-6 h-6 text-blue-500' })}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-800 truncate">{pendingFile.name}</p>
                <p className="text-xs text-slate-400">{formatFileSize(pendingFile.size)}</p>
              </div>
              {/* Upload progress or cancel */}
              {uploading ? (
                <div className="flex items-center gap-2 flex-shrink-0">
                  <div className="w-20 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                    <div className="h-full bg-blue-500 rounded-full transition-all duration-300" style={{ width: `${uploadProgress}%` }} />
                  </div>
                  <span className="text-xs text-slate-400">{uploadProgress}%</span>
                </div>
              ) : (
                <div className="flex items-center gap-1 flex-shrink-0">
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-red-500" onClick={() => setPendingFile(null)} data-testid="cancel-file-btn">
                    <X className="w-4 h-4" />
                  </Button>
                  <Button size="icon" className="h-8 w-8 bg-blue-500 hover:bg-blue-600" onClick={uploadPendingFile} data-testid="confirm-upload-btn">
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Input */}
        <div className="px-3 py-2 border-t border-slate-200 bg-white flex-shrink-0">
          <div className="flex items-center gap-2">
            <input type="file" ref={fileInputRef} className="hidden"
              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip,.rvt,.dwg,.csv"
              onChange={handleFileInput} />
            <Button variant="ghost" size="icon"
              className="h-9 w-9 text-slate-500 hover:text-blue-500 flex-shrink-0"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              data-testid="attach-file-btn"
            >
              <Paperclip className="w-4 h-4" />
            </Button>
            <Input
              value={text} onChange={(e) => setText(e.target.value)}
              onKeyDown={handleKeyDown} onPaste={handlePaste}
              placeholder="Escribe un mensaje..."
              className="flex-1 h-9 text-sm"
              disabled={sending || uploading}
              data-testid="message-input"
            />
            <Button size="icon" className="h-9 w-9 bg-blue-500 hover:bg-blue-600 flex-shrink-0"
              onClick={() => pendingFile ? uploadPendingFile() : handleSend()}
              disabled={(!text.trim() && !pendingFile) || sending || uploading}
              data-testid="send-message-btn"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-[10px] text-slate-300 mt-1 text-center">Arrastra archivos aquí o pega imágenes</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
